<template>
  <span>
    <v-container
      fluid
      class="pa-0"
    >
      <v-row no-gutters v-if="bidRatesBelowBase" class="mb-1">
        <v-col cols="auto" class="d-flex align-center">
          <v-icon small color="warning" class="mr-1">mdi-alert</v-icon>
          <span class="caption">{{ $t('bidRatesBelowActivityBaseRate') }}</span>
        </v-col>
      </v-row>
      <v-data-table
        id="bundle-activity-definition-table"
        :items="filteredBuilders"
        :headers="headers"
        :expanded.sync="expanded"
        :hide-default-footer="filteredBuilders.length < 10"
        class="scrollable-table"
        item-key="activityBuilderId"
        fixed-header
        height="min(60vh, max(256px, calc(100vh - 400px)))"
        :item-class="rowClass"
      >

        <template #item.activityStatus="{ item }">
          <Icon
            :icon="IconHelpers.getIconForObjectType(accountingCategoryFromInt(item.category))"
            :tooltipText="accountingCategoryFromInt(item.category)"
            :small="false"
          />

          <Icon
            :icon="item.templateStatus === 1 ? 'mdi-cancel' : 'mdi-checkbox-marked-circle'"
            :iconColor="item.templateStatus === 1 ? 'black' : 'success'"
            :tooltipColor="item.templateStatus === 1 ? 'black' : 'success'"
            :tooltipText="item.templateStatus === 1 ? $t('inactive') : $t('active')"
            :small="false"
          />

          <Icon
            icon-color="info"
            :icon="costTypeIconFromInt(item.costBehavior)"
            :tooltipText="costTypeFromBuilder(item)"
            :small="false"
          />

          <Icon
          v-if="TemplateSpecialization.forInt(item.specialization).icon"
          :icon="TemplateSpecialization.forInt(item.specialization).icon"
          :tooltipText="TemplateSpecialization.forInt(item.specialization).name"
          :small="false"/>

          <Icon
            v-if="item.deductingBuilderIds?.length > 0"
            icon="mdi-minus-circle-multiple-outline"
            :tooltipText="deductionParentTooltip(item)"
            :small="false"
            iconColor="error"
            @mouseenter.native="setHoveredDeducting(item)"
            @mouseleave.native="clearHoveredDeducting"
            />
          <Icon
            v-if="builderDeducts(item)"
            icon="mdi-circle-multiple-outline"
            :tooltipText="deductionChildTooltip(item)"
            :small="false"
            iconColor="success"
            @mouseenter.native="setHoveredParent(item)"
            @mouseleave.native="clearHoveredParent"
            />
        </template>

        <template #item.counterpartyName="{ item }">
          <v-row>
            <span
              v-if="builderIsDynamicCounterparty(item)"
              :style="`background-color: ${ runtimeFieldResolvesToDuplicateCounterparty(item.activityTemplateId, item.runtimeField) ? '#ff000025' : 'unset'};`"
            >
              <v-col>
                <Icon
                  icon="mdi-lock-open"
                  iconColor="black"
                  :tooltipText="$t('switchToConcreteCounterparty')"
                  :small="false"
                  @icon-clicked="{ item.runtimeField = undefined, item.recoveryMode = RecoveryMode.None }"
                  elementId="switch-to-concrete-counterparty"
                />

                <Icon
                  icon="mdi-alpha-r-circle-outline"
                  :iconColor="!rcpCanResolve(item.runtimeField) ? 'warning' : 'info'"
                  :small="false"
                  :tooltipText="counterpartyTextFromRuntimeField(item.runtimeField)"
                  :tooltipColor="!rcpCanResolve(item.runtimeField) ? 'warning' : 'info'"
                />

                {{ runtimeCounterparty(item.runtimeField) }}

                <Icon
                  v-if="runtimeFieldResolvesToDuplicateCounterparty(item.activityTemplateId, item.runtimeField)"
                  icon="mdi-alert"
                  :small="false"
                  :tooltipText="$t('duplicateActivityBuilder')"
                />
              </v-col>
            </span>
            <v-autocomplete
              v-else
              :background-color="counterpartyIsDuplicate(item.activityTemplateId, item.counterparty?.accountId) ? '#ff000025' : 'unset'"
              v-model="item.counterparty"
              item-text="name"
              item-value="name"
              return-object
              :persistent-hint="counterpartyIsDuplicate(item.activityTemplateId, item.counterparty?.accountId)"
              :hint="counterpartyIsDuplicate(item.activityTemplateId, item.counterparty?.accountId) ? $t('duplicateActivityBuilder') : ''"
              :placeholder="$t('account')"
              @input="duplicateCounterparties = getDuplicateCounterparties()"
              @change="item.recoveryMode = recoveryModeFromBuilder(item)"
              :items="allAccounts"
              id="counterparty"
            >
              <template #prepend v-if="!nullOrUndefined(item.runtimeFieldFromTemplate)">
                <Icon
                  icon="mdi-arrow-u-left-top-bold"
                  :small="false"
                  iconColor="black"
                  :tooltipText="$t('switchToRuntimeCounterparty')"
                  @icon-clicked="{ item.runtimeField = item.runtimeFieldFromTemplate, item.recoveryMode = defaultRecoveryModeFromAccountingCategory(item), delete item.counterparty }"
                  elementId="switch-to-runtime-counterparty"
                />
              </template>
            </v-autocomplete>
          </v-row>
        </template>

        <template #item.recovers="{ item }">
          <v-row>
            <Icon
              v-if="!builderIsDynamicCounterparty(item) && builderCanRecover(item) && (item.recoveryMode.value === RecoveryMode.None.value)"
              icon="mdi-alert"
              iconColor="warning"
              :small="false"
              :tooltipText="$t('counterpartyHasOutstandingAdvance')"
            />

            <Icon
              v-if="builderIsDynamicCounterparty(item) && builderCanRecover(item)"
              icon="mdi-help-circle-outline"
              iconColor="info"
              :tooltipText="$t('runtimeCounterpartyCannotRecoverFromSpecificAdvance')"
            />

            <v-autocomplete
              :disabled="recoveryModeDisabled(item)"
              v-model="item.recoveryMode"
              :label="$t('recoveryMode')"
              return-object
              :items="mappedRecoveryModesByBuilder(item)"
              item-text="name"
              id="recovery-mode"
              @change="item.advanceId = undefined"
            >
              <template #item="{item}">
                {{ item.name }}
                <v-spacer/>
                <v-tooltip
                  bottom
                  color="primary"
                  v-if="item.definition"
                >
                  <template #activator="{on}">
                    <v-icon
                      right
                      color="primary"
                      v-on="on"
                      small
                    >
                      mdi-help-circle-outline
                    </v-icon>
                  </template>
                  <span
                    class="subtitle-1 white--text"
                    style="white-space: pre-line"
                    disabled
                  >
                    {{ $t(item.definition, { tract: tract.name })}}
                  </span>
                </v-tooltip>
              </template>
            </v-autocomplete>
          </v-row>
        </template>

        <template #item.businessEntityName="{ item }">
          {{ entityNameForBuilder(item) }}
        </template>

        <template #item.costBehavior="{ item }">
          {{ costBehaviorFromInt(item.costBehavior) }}
        </template>

        <template #item.baseRate="{ item }">
          <MoneyTextField
            :disabled="isObligationBuilder(item)"
            v-if="!isApOrJePenalty(item)"
            :initialValue="item.baseRate"
            :itemId="item.activityBuilderId"
            :label="$t('rate')"
            :expectedValue="expectedValue(item.activityTemplateId)"
            dense
            @blur-entered="baseRateEnteredForActivity"
            :extraRules="['validActivityRate']"
            elementId="base-rate"
          />

          <div
            class="py-5"
            v-else
          >
            {{$t('notApplicable')}}
          </div>
        </template>

        <template #item.rateSheet="{ item: activityBuilder }">
          <v-text-field
          class="mt-3"
          v-if="activityBuilder.payBy === PayBy.Volume.value && rateSheetRerenderTrigger"
          :value="activityBuilder?.rateSheet ? activityBuilder.rateSheet.name : ''"
          readonly
          dense
          :label="$t('rateSheet')"
          @click="$event.preventDefault()"
          @mouseup="$event.preventDefault()"
          @mousedown="defineRateSheetClicked(activityBuilder)"
          >
            <template #append>
              <Icon
                v-if="!isObligationBuilder(activityBuilder)"
                icon="mdi-pencil"
                :tooltipText="$t('defineRateSheet')"
                margin=""
                :small="false"
                @icon-clicked="defineRateSheetClicked(activityBuilder)"
              />
            </template>
          </v-text-field>
        </template>

        <template #item.payOn="{ item }">
          {{ payOnFromInt(item.payOn)}}
        </template>

        <template #item.payBy="{ item }">
          {{ payByFromInt(item.payBy) }}
        </template>

        <template #item.modifier="{ item }">
          {{ modifierFromInt(item.modifier) }}
          <Icon
          v-if="warnMissingDestinationDistance(item)"
          icon="mdi-alert"
          iconColor="warning"
          :tooltipText="$t('distanceUndefined')"/>
        </template>

        <template #item.actions="{ item }">
          <Icon
          :disabled="editDeductionsDisabled(item)"
          icon="mdi-minus-circle-multiple-outline"
          :tooltipText="$t('editDeductingActivities')"
          :small="false"
          @icon-clicked="editBuilderDeductions(item)"
          />
        </template>

        <template v-slot:expanded-item="{ headers, item: builder }">
          <td
            :key="builder.activityBuilderId"
            :colspan="headers.length + 2"
            v-if="showExpansion(builder)"
            id="expanded-bundle-activity"
          >
            <v-container fluid>
              <v-row
                v-if="builder.recoveryMode.value === RecoveryMode.Specific.value && !builderIsDynamicCounterparty(builder)"
                no-gutters
              >
                <v-col cols="12" sm="6" lg="4">
                  <AdvanceAutocomplete
                    :advanceId="builder.advanceId"
                    :accountId="builder.counterparty.accountId"
                    @advance-chosen="advance => setAdvanceForBuilder(builder, advance)"
                    :filter="a => a.recoveryStrategy === RecoveryStrategy.Ticket.value && recoveryTypeAllowedForPayBy(a.recoveryType, builder.payBy)"
                  />
                </v-col>
              </v-row>

              <v-row
                v-if="!builder.limitToBaseRate && products?.length > 0"
                align="center"
              >
                <div
                  v-if="builder.products?.length === 0"
                  style="width: 30px; height: 150px"
                />

                <v-col
                  v-for="activityDetailProduct in builder.products"
                  :key="activityDetailProduct.productId"
                  cols="12"
                  md="4"
                  lg="3"
                  xl="2"
                >
                  <v-card id="product-detail-card">
                    <v-card-title class="py-0 px-2">
                      <span
                        style="font-size: 14px;"
                        class="pa-1 ma-0"
                      >
                        {{activityDetailProduct.productName}}
                      </span>
                      <v-spacer></v-spacer>
                      <Icon
                        v-if="activityDetailProduct.inheritedFromCruise === true"
                        iconColor="info"
                        icon="mdi-forest"
                        :tooltipText="$t('inheritedFromCruise')"
                      />

                      <Icon
                        v-if="activityDetailProduct.inheritedFromBid === true"
                        :iconColor="divergesFromBid(activityDetailProduct) ? 'warning' : 'info'"
                        icon="mdi-gavel"
                        :tooltipText="divergesFromBid(activityDetailProduct)
                          ? $t('divergesFromWinningBid', { rate: formatMoney(activityDetailProduct.bidRate) })
                          : $t('inheritedFromWinningBid')"
                      />

                      <Icon
                        icon="mdi-close"
                        iconColor="grey"
                        elementId="close"
                        @icon-clicked="removeProductDetail(builder, activityDetailProduct)"
                        :tooltipText="$t('removeProductDetail')"
                      />
                    </v-card-title>

                    <v-card-text>
                      <MoneyTextField
                        :initialValue="activityDetailProduct.baseRate"
                        :itemId="`${builder.activityBuilderId}-${activityDetailProduct.productId}`"
                        :minimumValueConfig="{ min: builder.baseRate, error: $t('baseRateLowerThanActivity') }"
                        :label="$t('rate')"
                        :expectedValue="expectedValue(builder.activityTemplateId, activityDetailProduct.productId)"
                        dense
                        @blur-entered="baseRateEnteredForProduct"
                        :extraRules="['validActivityRate']"
                        elementId="rate"
                      />
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="auto">
                  <v-menu v-if="products.length > 0">
                    <template #activator="{ on: menu }">
                      <v-tooltip
                        bottom
                        color="grey"
                      >
                        <template #activator="{ on: tooltip }">
                          <v-icon
                            v-on="{ ...menu, ...tooltip }"
                            v-show="missingProducts(builder, products).length > 0"
                            height="24"
                            v-if="products.length > 0"
                            id="add-product"
                          >
                            mdi-plus
                          </v-icon>
                        </template>
                        <span class="subtitle-1 white--text">
                          {{$t('addProductDetail')}}
                        </span>
                      </v-tooltip>
                    </template>
                    <v-list>
                      <v-list-item
                        v-for="product in missingProducts(builder, products)"
                        :key="product.productId"
                        link
                        @click="addProductDetail(builder, product)"
                      >
                        {{product.name}}
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
              </v-row>
            </v-container>
          </td>
        </template>
      </v-data-table>

      <Dialog
        :stateId="dialogId"
        @close="dialogClosed"
        @click:outside="dialogClosed"
        @dialog-closing="dialogClosed"
      >
        <RateSheetEditor
          v-if="rateSheetOpen"
          :accountId="tractDestination.destinationAccountId"
          :hideDefinition="selectedBuilder.specialization === TemplateSpecialization.Obligation.value"
          :existingRateSheet="selectedBuilder?.rateSheet"
          :activity="selectedBuilder"
          @confirm="rateSheetConfirmed"
          @cancel="dialogClosed"
        />
        <DeductingActivities
          v-if="editDeductionsDialog"
          :contractType="contractType"
          :propBuilder="focusedBuilder"
          :builders="filteredBuilders"
          :stringFromBuilder="counterpartyStringFromBuilder"
          @close="close"
          @update-deductions="updateDeductions"
        />
      </Dialog>
    </v-container>
  </span>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import TractHeaders from '@/headers/Tract'
import { TractDestination } from '@/model/TractDestination'
import {
  AccountingCategory,
  ContractType,
  CostType,
  RuntimeCounterparty,
  RecoveryMode,
  TemplateSpecialization,
  RecoveryStrategy,
  ActivityModifier,
  PayBy,
  ProductionCostType,
  TemplateAccountingCategory,
  NamedRateSpecialization
} from '@/utils/Enumerations.js'
import {
  runtimeCounterparty, costTypeFromInt, payOnFromInt, costTypeIconFromInt,
  activityStatusFromInt, costBehaviorFromInt, payByFromInt, recoveryTypeAllowedForPayBy
} from '@/utils/FinancialHelpers.js'
import { indexArrayBy } from '@/utils/base'
import { formatMoney } from '@/utils/NumericMutations.js'
import { TemplateSpecializationInfo } from '@/components/activity/ActivityMapping'
import { uniqueDialogId } from '@/utils/componentHelpers'
import IconHelpers from '@/utils/IconHelpers'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'BundleActivityDefinition',

  props: {
    tract: Object,
    tractDestination: TractDestination,
    propBundle: Object,
    products: Array,
    activityTemplates: Array,
    limitToTractLevelCapture: Boolean,
    useCruiseRateForStumpage: Boolean,
    templateStats: {
      type: Map,
      required: false,
      default: new Map()
    },
    contractAccount: Object,
    landowners: [],
    contractType: Object,
    namedRates: {
      type: Array,
      default: () => []
    },
    winningBid: {
      type: Object,
      default: null
    }
  },

  components: {
    MoneyTextField: () => import('@/components/helper/MoneyTextField'),
    AdvanceAutocomplete: () => import('@/components/autocomplete/AdvanceAutocomplete.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    RateSheetEditor: () => import('@/components/activity/rate-sheet/RateSheetEditor.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    DeductingActivities: () => import('@/components/tract/tract-form/DeductingActivities.vue')
  },

  data: () => ({
    dialogId: uniqueDialogId('bundle-activity-definition-dialog'),
    IconHelpers,
    TemplateSpecializationInfo,
    TemplateSpecialization,
    TemplateAccountingCategory,
    PayBy,
    AccountingCategory,
    RecoveryMode,
    RecoveryStrategy,
    loading: true,
    expanded: [],
    allBuilders: [],
    duplicateCounterparties: {},
    runtimeFieldCounterpartyIdMap: new Map(),
    selectedProduct: null,
    rateSheetOpen: false,
    rateSheetRerenderTrigger: true,
    selectedBuilder: null,
    focusedBuilder: undefined,
    hoveredDeductingBuilderIds: [],
    hoveredParentBuilderIds: [],
    editDeductionsDialog: false,
    rateSheetById: new Map()
  }),

  watch: {
    allBuilders: {
      handler (val) {
        this.duplicateCounterparties = this.getDuplicateCounterparties()
        this.$emit('activity-builders-updated', val)
      },
      deep: true
    },

    filteredBuilders: {
      handler (val) {
        this.expanded = [...val]
        this.duplicateCounterparties = this.getDuplicateCounterparties()
      },
      deep: true
    },

    duplicateCounterparties: {
      handler (val) {
        this.$emit('duplicate-counterparties', val)
      },
      deep: true
    },

    useCruiseRateForStumpage (val) {
      this.toggleCruiseRates(val)
    },

    products: {
      async handler (current, previous) {
        if (previous == null || previous.length === 0) {
          await this.initializeBuilders()
          return
        }
        this.filterDetails()
      },
      deep: true,
      immediate: true
    },

    contractAccount: {
      handler () {
        this.initializeCounterpartyRuntimeFieldMap()
        this.duplicateCounterparties = this.getDuplicateCounterparties()
      },
      deep: true
    }
  },

  computed: {
    ...mapGetters('account', ['allAccounts']),
    ...mapGetters('rate-sheet-definition', ['accountRateSheetDefinitions']),
    ...mapGetters('user', ['businessEntities']),

    entityNameById () {
      return this.businessEntities.reduce((acc, entity) => {
        acc[entity.businessEntityId] = entity.name
        return acc
      }, {})
    },

    filteredBuilders () {
      return this.allBuilders.filter(b => !this.limitToTractLevelCapture || b.runtimeField || b.tractLevelCapture)
    },

    tractContents () {
      return this.tract.tractContents
    },

    headers () {
      let result = TractHeaders.templateBundleHeaders()
      const hasPayByVolumeActivity = this.allBuilders.some(b => b.payBy === PayBy.Volume.value)
      if (!hasPayByVolumeActivity) {
        result = result.filter(h => h.value !== 'rateSheet')
      }
      return result
    },

    isStumpageTract () {
      return this.tract.type.category === 0
    },

    accountIndices () {
      return this.indexArrayBy(this.allAccounts, a => a.accountId)
    },

    isByVolumeContract () {
      return this.propBundle?.activityBuilders.some(ab => ab.payBy === PayBy.Volume.value)
    },

    bidRatesBelowBase () {
      return this.allBuilders.some(b => (b.products ?? []).some(p => p.inheritedFromBid && p.baseRate < parseFloat(b.baseRate)))
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('tract', ['fetchTractLandowners']),
    ...mapActions('rate-sheet-definition', ['fetchAccountRateSheetDefinitions']),
    ...mapActions('rate-sheet', ['fetchRateSheet']),
    ...mapActions('user', ['fetchAllBusinessEntities']),

    indexArrayBy,
    formatMoney,
    costBehaviorFromInt,
    payByFromInt,
    recoveryTypeAllowedForPayBy,
    payOnFromInt,
    activityStatusFromInt,
    costTypeFromInt,
    runtimeCounterparty,
    costTypeIconFromInt,

    accountingCategoryFromInt: (category) => AccountingCategory.fromInt(category),

    entityNameForBuilder (builder) {
      return this.entityNameById[builder?.template?.businessEntityId] ?? ''
    },

    assignEntityNames () {
      this.allBuilders.forEach(builder => {
        builder.businessEntityName = this.entityNameForBuilder(builder)
      })
    },

    costTypeFromBuilder (builder) {
      const costType = CostType.forInt(builder.costBehavior)
      if (costType !== CostType.Production) return costType.name

      const productionCostType = ProductionCostType.forInt(builder.productionType)
      return productionCostType === ProductionCostType.Other
        ? costType.name
        : `${costType.name} - ${productionCostType.name}`
    },

    modifierFromInt (x) {
      return x === ActivityModifier.DistanceTraveled.value
        ? this.$t('distanceTraveledWithMiles', { distance: this.tractDestination.distance })
        : ActivityModifier.fromInt(x)
    },

    recoveryModeFromBuilder (builder) {
      return RecoveryMode.enums.find(rm => rm.value === builder.defaultRecoveryMode) || RecoveryMode.None
    },

    counterpartyTextFromRuntimeField (runtimeField) {
      if (runtimeField === RuntimeCounterparty.Driver.value) return this.$t('ticketDriver')
      const ids = this.runtimeFieldCounterpartyIdMap.get(runtimeField)
      switch (ids.length) {
        case 0:
          return this.$t('notSet')
        case 1:
          return this.findAccount(ids[0])?.name ?? this.$t('notSet')
        default:
          return this.$t('multiple')
      }
    },

    counterpartyStringFromBuilder (builder) {
      if (builder.runtimeField != null) return this.counterpartyTextFromRuntimeField(builder.runtimeField)
      return builder.counterparty?.name ?? builder.counterpartyName ?? this.$t('notSet')
    },

    nullOrUndefined: (val) => val === null || val === undefined,

    expectedValue (activityTemplateId, productId) {
      const activityStats = this.templateStats?.get(activityTemplateId)
      if (!productId) return activityStats?.baseRecentAverage
      return activityStats?.detailStats.find(p => p.productId === productId)?.recentAverage
    },

    baseRateEnteredForActivity ({ amount, itemId }) {
      const index = this.allBuilders.findIndex(a => a.activityBuilderId === itemId)

      if (index !== -1) {
        this.allBuilders[index].baseRate = amount
      }
    },

    toggleCruiseRates (shouldUseCruise) {
      const clonedBuilders = JSON.parse(JSON.stringify(this.allBuilders))
      this.allBuilders = []
      this.loading = true

      if (shouldUseCruise === true) {
        for (const builder of clonedBuilders) {
          if (this.shouldActivityUseCruiseData(builder)) {
            builder.products = builder.products.map(p => {
              const cruiseProduct = this.tractContents.find(tc => tc.productId === p.productId)
              return {
                productId: p.productId,
                productName: p.productName,
                inheritedFromCruise: cruiseProduct !== undefined,
                baseRate: cruiseProduct ? parseFloat(cruiseProduct.pricePerUnit) : parseFloat(builder.baseRate)
              }
            })
          }
        }
      } else {
        for (const builder of clonedBuilders) {
          if (this.shouldActivityUseCruiseData(builder)) {
            builder.products = builder.products.map(p => {
              const detail = builder.template.details.find(pd => pd.productId === p.productId)
              return {
                productId: p.productId,
                productName: p.productName,
                baseRate: detail ? parseFloat(detail.rate) : parseFloat(builder.baseRate)
              }
            })
          }
        }
      }

      this.$nextTick(() => {
        this.allBuilders = clonedBuilders
        this.loading = false
      })
    },

    mappedRecoveryModesByBuilder (builder) {
      return RecoveryMode.enums.map(recoveryMode => ({
        ...recoveryMode,
        name: recoveryMode.name,
        definition: recoveryMode.explanation,
        excluded: (recoveryMode.value === RecoveryMode.Specific.value && this.builderIsDynamicCounterparty(builder)) ||
          (recoveryMode.value === RecoveryMode.OldestOnTract.value && this.tract === null)
      })).filter(recoveryMode => !recoveryMode.excluded)
    },

    async initializeBuilders () {
      this.allBuilders = []
      this.loading = true
      if (!this.businessEntities?.length) {
        await this.fetchAllBusinessEntities()
      }
      const builders = cloneDeep(this.propBundle.activityBuilders)
      this.initializeCounterpartyRuntimeFieldMap()

      for (const activityBuilder of builders) {
        this.constructActivityBuilder(activityBuilder)
      }

      // Auto-apply obligation rates to obligation builders
      for (const builder of builders) {
        if (this.obligationBuilder(builder)) {
          await this.applyObligationRate(builder)
        }
      }

      this.$nextTick(() => {
        this.allBuilders = builders
        this.assignEntityNames()
        this.loading = false
      })
    },

    constructActivityBuilder (activityBuilder) {
      activityBuilder.template = this.activityTemplates.find(t => t.activityTemplateId === activityBuilder.activityTemplateId)
      if (activityBuilder.counterpartyId) activityBuilder.counterparty = this.findAccount(activityBuilder.counterpartyId)
      activityBuilder.runtimeFieldFromTemplate = activityBuilder.runtimeField
      activityBuilder.recoveryMode = this.recoveryModeFromBuilder(activityBuilder)
      activityBuilder.deductingBuilderIds = []
      this.constructProductsForActivityBuilder(activityBuilder)
    },

    constructProductsForActivityBuilder (activityBuilder) {
      if (this.noDetailsForActivity(activityBuilder)) {
        activityBuilder.products = []
      } else {
        activityBuilder.products = this.products
          .map(p => this.detailProductMapper(p, activityBuilder))
      }
    },

    detailProductMapper (product, activityBuilder) {
      const bidRate = this.bidRateForBuilder(activityBuilder, product.productId)
      if (bidRate !== undefined && bidRate !== null) {
        return {
          productId: product.productId,
          productName: product.name,
          inheritedFromBid: true,
          bidRate: parseFloat(bidRate),
          baseRate: parseFloat(bidRate)
        }
      }
      if (this.shouldActivityUseCruiseData(activityBuilder) && this.useCruiseRateForStumpage) {
        return this.constructCruiseProductDetail(product, activityBuilder)
      } else {
        return this.constructDetail(product, activityBuilder)
      }
    },

    divergesFromBid (detail) {
      return detail.bidRate !== undefined && parseFloat(detail.baseRate) !== detail.bidRate
    },

    builderInheritsBidRates (builder) {
      if (!this.winningBid) return false
      if (builder.category !== AccountingCategory.Payable.value) return false
      if (this.builderIsDynamicCounterparty(builder)) {
        return builder.runtimeField === RuntimeCounterparty.Supplier.value &&
          this.tract.supplierAccountId === this.winningBid.supplierAccountId
      }
      return builder.counterparty?.accountId === this.winningBid.supplierAccountId
    },

    bidRateForBuilder (builder, productId) {
      if (!this.builderInheritsBidRates(builder)) return undefined
      return this.winningBid.rateByProductId[productId]
    },

    noDetailsForActivity (activityBuilder) {
      return activityBuilder.limitToBaseRate || this.isApOrJePenalty(activityBuilder) || activityBuilder.payBy === PayBy.Volume.value
    },

    constructCruiseProductDetail (product, activityBuilder) {
      const cruiseProduct = this.tractContentByProductId(product.productId)
      return {
        productId: product.productId,
        productName: product.name,
        inheritedFromCruise: !!cruiseProduct,
        baseRate: parseFloat(cruiseProduct?.pricePerUnit ?? activityBuilder.baseRate)
      }
    },

    tractContentByProductId (productId) {
      return this.tractContents.find(tc => tc.productId === productId)
    },

    constructDetail (product, activityBuilder) {
      const detail = activityBuilder.template.details.find(pd => pd.productId === product.productId)
      return {
        productId: product.productId,
        productName: product.name,
        baseRate: detail ? parseFloat(detail.rate) : parseFloat(activityBuilder.baseRate)
      }
    },

    detailByProductId (productId, activityBuilder) {
      return activityBuilder.template.details.find(p => p.productId === productId)
    },

    initializeCounterpartyRuntimeFieldMap () {
      this.runtimeFieldCounterpartyIdMap = new Map()
      const valueOrEmptyArray = (value) => { return !this.nullOrUndefined(value) ? [value] : [] }
      this.runtimeFieldCounterpartyIdMap.set(RuntimeCounterparty.Landowner.value, this.landowners.map(lo => lo.accountId))
      this.runtimeFieldCounterpartyIdMap.set(RuntimeCounterparty.DefaultLogger.value, valueOrEmptyArray(this.tract.loggerAccountId))
      this.runtimeFieldCounterpartyIdMap.set(RuntimeCounterparty.ContractAccount.value, valueOrEmptyArray(this.contractAccount?.accountId))
      this.runtimeFieldCounterpartyIdMap.set(RuntimeCounterparty.Hauler.value, valueOrEmptyArray(this.tract.haulerAccountId))
      this.runtimeFieldCounterpartyIdMap.set(RuntimeCounterparty.Supplier.value, valueOrEmptyArray(this.tract.supplierAccountId))
      this.runtimeFieldCounterpartyIdMap.set(RuntimeCounterparty.DestinationAccount.value, valueOrEmptyArray(this.tractDestination.destinationAccountId))
      this.runtimeFieldCounterpartyIdMap.set(RuntimeCounterparty.ConsultingForester.value, valueOrEmptyArray(this.tract.consultingForesterAccountId))
    },

    builderCanRecover (builder) {
      return (this.builderIsDynamicCounterparty(builder) || (builder.counterparty?.hasAdvances ?? builder.counterpartyHasAdvances ?? true)) &&
        builder.template.category === AccountingCategory.Payable.value &&
        TemplateSpecializationInfo.get(builder.specialization).baseCost !== false &&
        builder.template.specialization !== TemplateSpecialization.Penalty.value
    },

    rcpCanResolve (rcp) {
      if (rcp === RuntimeCounterparty.Driver.value) return true
      const ids = this.runtimeFieldCounterpartyIdMap.get(rcp)
      return ids.length > 0
    },

    warnMissingDestinationDistance (builder) {
      return (builder.modifier === ActivityModifier.DistanceTraveled.value && (this.tractDestination.distance === null || this.tractDestination.distance === 0))
    },

    addProductDetail (builder, product) {
      const bidRate = this.bidRateForBuilder(builder, product.productId)
      const inheritedFromBid = bidRate !== undefined && bidRate !== null
      const newDetail = {
        productId: product.productId,
        productName: product.name,
        inheritedFromBid,
        bidRate: inheritedFromBid ? parseFloat(bidRate) : undefined,
        baseRate: inheritedFromBid ? parseFloat(bidRate) : builder.baseRate
      }

      builder.products.push(newDetail)
    },

    removeProductDetail (builder, detail) {
      const builderIndex = this.allBuilders.findIndex(b => b.activityBuilderId === builder.activityBuilderId)
      if (builderIndex === -1) return
      const productIndex = this.allBuilders[builderIndex].products.findIndex(pd => pd.productId === detail.productId)
      if (productIndex === -1) return
      const currentProducts = this.allBuilders[builderIndex].products
      this.allBuilders[builderIndex].products = [
        ...currentProducts.slice(0, productIndex),
        ...currentProducts.slice(productIndex + 1)
      ]
    },

    missingProducts (builder, products) {
      const cannotHaveProducts = this.noDetailsForActivity(builder)
      if (cannotHaveProducts) return []

      function productIsMissing (p) {
        return !(builder?.products ?? []).some(bp => bp.productId === p.productId)
      }

      return products.filter(productIsMissing)
    },

    baseRateEnteredForProduct ({ amount, itemId }) {
      let [activityBuilderId, productId] = itemId.split('-')
      activityBuilderId = parseInt(activityBuilderId)
      productId = parseInt(productId)

      const activityIndex = this.allBuilders.findIndex(a => a.activityBuilderId === activityBuilderId)

      if (activityIndex !== -1) {
        const productIndex = this.allBuilders[activityIndex].products.findIndex(d => d.productId === productId)

        if (productIndex !== -1) {
          this.allBuilders[activityIndex].products[productIndex].baseRate = amount
        }
      }
    },

    findAccount (accountId) {
      return this.allAccounts[this.accountIndices[accountId]]
    },

    shouldActivityUseCruiseData (activityBuilder) {
      return activityBuilder.category !== 1 && activityBuilder.costBehavior === 2
    },

    getDuplicateCounterparties () {
      const counts = {}
      const duplicates = {}
      for (const builder of this.allBuilders) {
        const activityTemplateId = builder?.activityTemplateId
        let targetCounterpartiesIds = !this.nullOrUndefined(builder.runtimeField) ? this.runtimeFieldCounterpartyIdMap.get(builder.runtimeField) : [builder?.counterparty?.accountId]
        targetCounterpartiesIds ??= []
        targetCounterpartiesIds.forEach(tcp => {
          if (tcp !== undefined) {
            const key = `${activityTemplateId}-${tcp}`
            counts[key] = (counts[key] !== undefined) ? counts[key] + 1 : 1
          }
        })
      }
      Object.keys(counts).forEach(key => {
        if (counts[key] > 1) {
          duplicates[key] = true
        }
      })
      return duplicates
    },

    counterpartyIsDuplicate (activityTemplateId, counterpartyId) {
      if (this.nullOrUndefined(counterpartyId)) return false
      return this.duplicateCounterparties[`${activityTemplateId}-${counterpartyId}`] ?? false
    },

    runtimeFieldResolvesToDuplicateCounterparty (activityTemplateId, runtimeField) {
      if (runtimeField === RuntimeCounterparty.Driver.value) return false
      const ids = this.runtimeFieldCounterpartyIdMap.get(runtimeField)
      return ids.some(id => this.duplicateCounterparties[`${activityTemplateId}-${id}`] ?? false)
    },

    setAdvanceForBuilder (builder, advance) {
      builder.advanceId = advance?.advanceId
    },

    builderIsDynamicCounterparty (builder) {
      return (builder.runtimeField !== undefined && builder.runtimeField !== null)
    },

    defaultRecoveryModeFromAccountingCategory (item) {
      return item.category === AccountingCategory.Payable.value ? RecoveryMode.Oldest : RecoveryMode.None
    },

    isPenalty (item) {
      return TemplateSpecialization.Penalty.value === item.specialization
    },

    isApOrJePenalty (item) {
      return TemplateSpecialization.Penalty.value === item.specialization && [AccountingCategory.Payable.value, AccountingCategory.Accrual.value].includes(item.category)
    },

    showExpansion (builder) {
      const isVolume = builder.payBy === PayBy.Volume.value
      const canDefineProductRates = !builder.limitToBaseRate && this.products?.length > 0 && !isVolume
      const isInventory = builder.costBehavior !== CostType.NonInventory.value

      const showAdvanceAutoselect = builder.recoveryMode.value === RecoveryMode.Specific.value
      const showProductDetailEdit = canDefineProductRates && isInventory && !this.isApOrJePenalty(builder)

      return showProductDetailEdit || showAdvanceAutoselect
    },

    defineRateSheetClicked (builder) {
      if (this.isObligationBuilder(builder)) return
      this.selectedBuilder = builder
      this.rateSheetOpen = true
      this.openOrUpdateDialog(this.dialogId)
    },

    rateSheetConfirmed (rateSheet) {
      this.selectedBuilder.rateSheet = rateSheet
      this.rateSheetRerenderTrigger = false
      this.redrawRateSheets()
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    redrawRateSheets () { // TODO: Is this really the best way?
      this.rateSheetRerenderTrigger = false
      this.$nextTick(() => {
        this.rateSheetRerenderTrigger = true
      })
    },

    dialogClosed () {
      this.selectedBuilder = null
      this.rateSheetOpen = false
      this.editDeductionsDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    filterDetails () {
      for (const builder of this.allBuilders) {
        builder.products = builder.products.filter(p => this.products.some(prod => prod.productId === p.productId))
      }
    },

    editBuilderDeductions (builder) {
      if (this.contractType?.value !== ContractType.Production.value) {
        this.setSnackError(this.$t('onlyProductionContractsCanHaveDeductions'))
        return
      }

      if (this.isStumpageTract) {
        this.setSnackError(this.$t('onlyDeliveredTractsCanHaveDeductions'))
        return
      }

      const anotherWithDeductions = (this.allBuilders || []).find(b =>
        b?.activityBuilderId !== builder?.activityBuilderId && (b?.deductingBuilderIds?.length || 0) > 0
      )
      if (anotherWithDeductions) {
        this.setSnackError(this.$t('onlyOneBuilderMayHaveDeductions'))
        return
      }

      if (builder.category !== TemplateAccountingCategory.AccountsPayable.value) {
        this.setSnackError(this.$t('onlyApCanBeDeducted'))
        return
      }

      if (builder.specialization !== TemplateSpecialization.None.value) {
        this.setSnackError(this.$t('cannotDeductFromSpecializedActivity'))
        return
      }

      if (builder.costBehavior !== CostType.Production.value) {
        this.setSnackError(this.$t('onlyProductionActivitiesCanHaveDeductions'))
        return
      }

      if (this.allBuilders.some(b => b.deductingBuilderIds?.includes(builder.activityBuilderId))) {
        this.setSnackError(this.$t('deductingActivitiesCannotHaveDeductions'))
        return
      }

      const filteredBuilders = this.allBuilders.filter(b =>
        b.activityBuilderId !== builder.activityBuilderId &&
        b.category === TemplateAccountingCategory.AccountsPayable.value &&
        b.specialization === TemplateSpecialization.None.value &&
        b.deductingBuilderIds.length === 0
      )

      if (filteredBuilders.length === 0) {
        this.setSnackError(this.$t('noAvailableActivitiesToDeductFrom'))
        return
      }

      if (filteredBuilders.length > 0 && filteredBuilders.every(fb => fb.recoveryMode.value !== RecoveryMode.None.value)) {
        this.setSnackError(this.$t('deductingActivitiesCannotRecover', { activityTemplateName: builder.activityTemplateName }))
        return
      }

      if (filteredBuilders.length > 0 && filteredBuilders.every(fb => fb.runtimeField === RuntimeCounterparty.Landowner.value)) {
        this.setSnackError(this.$t('deductingActivitiesCannotPayLandowner', { activityTemplateName: builder.activityTemplateName }))
        return
      }

      this.focusedBuilder = builder
      this.editDeductionsDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '900' })
    },

    hasDeductingChildren (builder) {
      return (builder?.deductingBuilderIds || []).length > 0
    },

    getDeductingChildren (builder) {
      const ids = builder?.deductingBuilderIds || []
      return (this.allBuilders || []).filter(b => ids.includes(b.activityBuilderId))
    },

    getParents (builder) {
      const id = builder?.activityBuilderId
      return (this.allBuilders || []).filter(b => (b?.deductingBuilderIds || []).includes(id))
    },

    hasParent (builder) {
      return this.getParents(builder).length > 0
    },

    setHoveredDeducting (builder) {
      this.hoveredDeductingBuilderIds = this.getDeductingChildren(builder).map(b => b.activityBuilderId)
    },

    clearHoveredDeducting () {
      this.hoveredDeductingBuilderIds = []
    },

    setHoveredParent (builder) {
      this.hoveredParentBuilderIds = this.getParents(builder).map(b => b.activityBuilderId)
    },

    clearHoveredParent () {
      this.hoveredParentBuilderIds = []
    },

    deductionParentTooltip (builder) {
      const children = this.getDeductingChildren(builder)
      if (children.length === 0) return this.$t('paymentDeducted')
      const shown = children.slice(0, 2)
      const names = shown.map(c => `${c.activityTemplateName} (${this.counterpartyStringFromBuilder(c)})`).join(', ')
      const more = children.length > 2 ? `, +${children.length - 2} more` : ''
      return this.$t('deductedBy', { by: `${names}${more}` })
    },

    deductionChildTooltip (builder) {
      const parents = this.getParents(builder)
      if (parents.length === 0) return ''
      const shown = parents.slice(0, 1)
      const names = shown.map(p => `${p.activityTemplateName} (${this.counterpartyStringFromBuilder(p)})`).join(', ')
      const more = parents.length > 1 ? `, +${parents.length - 1} more` : ''
      return this.$t('deductsFrom', { from: `${names}${more}` })
    },

    rowClass (builder) {
      const id = builder.activityBuilderId
      const classes = []
      if (this.hoveredDeductingBuilderIds.includes(id)) classes.push('bundle-row-red-tint')
      if (this.hoveredParentBuilderIds.includes(id)) classes.push('bundle-row-green-tint')
      return classes.join(' ')
    },

    close () {
      this.focusedBuilder = undefined
      this.editDeductionsDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    updateDeductions (deductingBuilderIds) {
      this.focusedBuilder.deductingBuilderIds = deductingBuilderIds
      this.close()
    },

    builderDeducts (builder) {
      return this.allBuilders?.some(b => b.deductingBuilderIds?.includes(builder.activityBuilderId))
    },

    recoveryModeDisabled (builder) {
      return builder.template.category !== AccountingCategory.Payable.value ||
        (!this.builderIsDynamicCounterparty(builder) && !builder.counterparty?.hasAdvances) ||
        this.isPenalty(builder) ||
        this.builderDeducts(builder)
    },

    editDeductionsDisabled (builder) {
      return this.contractType?.value !== ContractType.Production.value ||
        this.isStumpageTract ||
        this.builderDeducts(builder) ||
        (this.filteredBuilders.some(b => b.deductingBuilderIds?.length > 0) && builder.deductingBuilderIds.length === 0)
    },

    obligationBuilder (builder) {
      return builder.template?.specialization === TemplateSpecialization.Obligation.value
    },

    async applyObligationRate (builder) {
      const obligationRate = this.namedRates.find(nr => nr.specialization === NamedRateSpecialization.Obligation.value)
      if (!obligationRate) {
        return
      }
      builder.payBy = obligationRate.costCalculation.payBy
      builder.payOn = obligationRate.costCalculation.payOn
      builder.baseRate = obligationRate.costCalculation.rate
      builder.namedRateId = obligationRate.namedRateId

      if (builder.payBy === PayBy.Volume.value) {
        const rateSheetId = obligationRate.costCalculation.rateSheetId
        if (!this.rateSheetById.has(rateSheetId)) {
          this.rateSheetById.set(rateSheetId, this.readRateSheet(rateSheetId))
        }
        builder.rateSheetId = rateSheetId
        builder.rateSheet = await this.rateSheetById.get(rateSheetId)
        builder.rateSheet.rateSheetDefinition = {
          name: builder.rateSheet.name
        }
        builder.products = []
        this.redrawRateSheets()
      }
    },

    async readRateSheet (rateSheetId) {
      if (!this.rateSheetById.has(rateSheetId)) {
        const rateSheet = await this.fetchRateSheet(rateSheetId)
        this.rateSheetById.set(rateSheetId, rateSheet)
      }
      return this.rateSheetById.get(rateSheetId)
    },

    isObligationBuilder (builder) {
      return builder.template?.specialization === TemplateSpecialization.Obligation.value
    }
  }
}
</script>

<style scoped>
.scrollable-table {
  max-height: calc(100vh - 400px);
  overflow-y: auto;
}
</style>

<style>
#bundle-activity-definition-table>.v-data-table__wrapper tbody tr.v-data-table__expanded__content {
  background-color:#eee;
  box-shadow: inset 0 0 0 0 #00000000;
  transition: .05s;
}

#bundle-activity-definition-table>.v-data-table__wrapper tbody tr.v-data-table__expanded__content:hover {
  background-color:#e0e0e0;
  box-shadow: inset 0 0 0 0 #00000000;
  transition: .05s;
}

#bundle-activity-definition-table .v-data-table__wrapper table tbody tr.bundle-row-red-tint > td {
  background-color: rgba(244, 67, 54, 0.12);
}

#bundle-activity-definition-table .v-data-table__wrapper table tbody tr.bundle-row-green-tint > td {
  background-color: rgba(76, 175, 80, 0.12);
}

#bundle-activity-definition-table .v-data-table__wrapper table tbody tr.bundle-row-red-tint + tr.v-data-table__expanded__content > td {
  background-color: rgba(244, 67, 54, 0.12);
}

#bundle-activity-definition-table .v-data-table__wrapper table tbody tr.bundle-row-green-tint + tr.v-data-table__expanded__content > td {
  background-color: rgba(76, 175, 80, 0.12);
}
</style>
